import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { apiFetch } from '@/api/client'
import type { AdminServer, AdminSettings, AdminUser, TwoFactorStatus } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { AppShell } from '@/ui/AppShell'
import {
  HairlineInput,
  HairlineTextarea,
  SidePanel,
  StatusDot,
} from '@/ui/primitives'

type Tab = 'servers' | 'users' | 'settings'

const emptyServerForm = {
  host: '',
  ssh_user: '',
  ssh_port: 22,
  role: '',
  notes: '',
  prompt_instructions: '',
  browser_enabled: false,
  browser_entry_url: '',
  is_active: true,
}

export function AdminPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('servers')
  const [servers, setServers] = useState<AdminServer[]>([])
  const [users, setUsers] = useState<AdminUser[]>([])
  const [adminSettings, setAdminSettings] = useState<AdminSettings | null>(null)
  const [sharedBasePrompt, setSharedBasePrompt] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [openServerPanel, setOpenServerPanel] = useState(false)
  const [editingServer, setEditingServer] = useState<AdminServer | null>(null)
  const [serverForm, setServerForm] = useState(emptyServerForm)

  const [openUserPanel, setOpenUserPanel] = useState(false)
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    role: 'user' as 'admin' | 'user',
    server_ids: [] as number[],
  })
  const [newPassword, setNewPassword] = useState('')

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/', { replace: true })
      return
    }
    void loadAll()
  }, [user, navigate])

  async function loadAll() {
    try {
      const [serverRows, userRows, settingsRow] = await Promise.all([
        apiFetch<AdminServer[]>('/v1/admin/servers'),
        apiFetch<AdminUser[]>('/v1/admin/users'),
        apiFetch<AdminSettings>('/v1/admin/settings'),
      ])
      setServers(serverRows)
      setUsers(userRows)
      setAdminSettings(settingsRow)
      setSharedBasePrompt(settingsRow.shared_base_prompt)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  function openNewServer() {
    setEditingServer(null)
    setServerForm(emptyServerForm)
    setOpenServerPanel(true)
  }

  function openEditServer(b: AdminServer) {
    setEditingServer(b)
    setServerForm({
      host: b.host,
      ssh_user: b.ssh_user,
      ssh_port: b.ssh_port,
      role: b.role,
      notes: b.notes,
      prompt_instructions: b.prompt_instructions,
      browser_enabled: b.browser_enabled,
      browser_entry_url: b.browser_entry_url,
      is_active: b.is_active,
    })
    setOpenServerPanel(true)
  }

  async function submitServer() {
    setBusy(true)
    setError(null)
    try {
      if (editingServer) {
        await apiFetch(`/v1/admin/servers/${editingServer.id}`, {
          method: 'PUT',
          body: JSON.stringify(serverForm),
        })
      } else {
        await apiFetch('/v1/admin/servers', {
          method: 'POST',
          body: JSON.stringify(serverForm),
        })
      }
      setOpenServerPanel(false)
      await loadAll()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function deleteServer(id: number) {
    if (!confirm('Delete this server? Existing sessions will stay on file.')) return
    setBusy(true)
    setError(null)
    try {
      await apiFetch(`/v1/admin/servers/${id}`, { method: 'DELETE' })
      await loadAll()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  function openNewUser() {
    setEditingUser(null)
    setUserForm({ email: '', password: '', role: 'user', server_ids: [] })
    setNewPassword('')
    setOpenUserPanel(true)
  }

  function openEditUser(e: AdminUser) {
    setEditingUser(e)
    setUserForm({
      email: e.email,
      password: '',
      role: e.role,
      server_ids: e.server_ids,
    })
    setNewPassword('')
    setOpenUserPanel(true)
  }

  async function submitUser() {
    setBusy(true)
    setError(null)
    try {
      if (editingUser) {
        await apiFetch(`/v1/admin/users/${editingUser.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            role: userForm.role,
            server_ids: userForm.server_ids,
          }),
        })
        if (newPassword.trim()) {
          await apiFetch(`/v1/admin/users/${editingUser.id}/password`, {
            method: 'POST',
            body: JSON.stringify({ password: newPassword.trim() }),
          })
        }
      } else {
        await apiFetch('/v1/admin/users', {
          method: 'POST',
          body: JSON.stringify({
            email: userForm.email,
            password: userForm.password,
            role: userForm.role,
            server_ids: userForm.server_ids,
          }),
        })
      }
      setOpenUserPanel(false)
      await loadAll()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function toggleUserStatus(row: AdminUser) {
    setBusy(true)
    setError(null)
    try {
      await apiFetch(
        `/v1/admin/users/${row.id}/${row.disabled_at ? 'enable' : 'disable'}`,
        { method: 'POST' },
      )
      await loadAll()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function submitSettings() {
    setBusy(true)
    setError(null)
    try {
      const next = await apiFetch<AdminSettings>('/v1/admin/settings', {
        method: 'PUT',
        body: JSON.stringify({ shared_base_prompt: sharedBasePrompt }),
      })
      setAdminSettings(next)
      setSharedBasePrompt(next.shared_base_prompt)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const serverLookup = useMemo(() => {
    const m = new Map<number, AdminServer>()
    for (const s of servers) m.set(s.id, s)
    return m
  }, [servers])

  return (
    <AppShell
      bureau={null}
      topBarRight={
        <button onClick={() => navigate('/')} className="btn btn-ghost">
          <span className="text-fg-dim">[</span>
          <span>← chat</span>
          <span className="text-fg-dim">]</span>
        </button>
      }
    >
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-6 font-mono animate-fade-in">
          {/* Page header */}
          <div className="mb-6">
            <div className="uppercase tracking-[0.08em] text-[12px] text-fg-dim">
              // admin
            </div>
            <h1 className="mt-1 font-mono font-semibold text-fg text-[24px] leading-tight">
              users, servers &amp; settings
            </h1>
            <p className="mt-1 text-[14px] text-fg-mute">
              Manage accounts, the shared base prompt, and per-server system prompts.
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-5 inline-flex items-center gap-0 border border-line">
            <TabBtn
              active={tab === 'servers'}
              onClick={() => setTab('servers')}
              count={servers.length}
            >
              servers
            </TabBtn>
            <span className="w-px self-stretch bg-line" />
            <TabBtn
              active={tab === 'users'}
              onClick={() => setTab('users')}
              count={users.length}
            >
              users
            </TabBtn>
            <span className="w-px self-stretch bg-line" />
            <TabBtn active={tab === 'settings'} onClick={() => setTab('settings')}>
              settings
            </TabBtn>
          </div>

          {error && (
            <div className="mb-5 border-l-2 border-danger pl-3">
              <div className="text-danger uppercase text-[11.5px] tracking-[0.08em]">
                ✗ error
              </div>
              <div className="mt-0.5 text-[14.5px] text-fg">{error}</div>
            </div>
          )}

          {tab === 'servers' ? (
            <ServersTable
              rows={servers}
              onNew={openNewServer}
              onEdit={openEditServer}
              onDelete={deleteServer}
            />
          ) : tab === 'users' ? (
            <UsersTable
              rows={users}
              serverLookup={serverLookup}
              onNew={openNewUser}
              onEdit={openEditUser}
              onToggle={toggleUserStatus}
            />
          ) : (
            <SettingsPanel
              sharedBasePrompt={sharedBasePrompt}
              updatedAt={adminSettings?.updated_at ?? null}
              busy={busy}
              onChange={setSharedBasePrompt}
              onSave={submitSettings}
            />
          )}
        </div>
      </div>

      {/* Server panel */}
      <SidePanel
        open={openServerPanel}
        onClose={() => setOpenServerPanel(false)}
        title={editingServer ? 'edit server' : 'add server'}
        subtitle={
          editingServer
            ? `#${editingServer.id} · ${editingServer.host}`
            : undefined
        }
      >
        <div className="space-y-4">
          <HairlineInput
            label="host"
            value={serverForm.host}
            onChange={(e) => setServerForm({ ...serverForm, host: e.target.value })}
            helper="hostname or ip"
            placeholder="web-01.prd.example.com"
          />
          <div className="grid grid-cols-2 gap-4">
            <HairlineInput
              label="ssh user"
              value={serverForm.ssh_user}
              onChange={(e) => setServerForm({ ...serverForm, ssh_user: e.target.value })}
              placeholder="ops"
            />
            <HairlineInput
              label="ssh port"
              type="number"
              value={serverForm.ssh_port}
              onChange={(e) =>
                setServerForm({ ...serverForm, ssh_port: Number(e.target.value) || 22 })
              }
              placeholder="22"
            />
          </div>
          <HairlineInput
            label="role"
            value={serverForm.role}
            onChange={(e) => setServerForm({ ...serverForm, role: e.target.value })}
            helper="free-text, e.g. web / db / staging"
            placeholder="production-web"
          />
          <HairlineInput
            label="notes"
            value={serverForm.notes}
            onChange={(e) => setServerForm({ ...serverForm, notes: e.target.value })}
            placeholder="short description shown to users"
          />
          <HairlineTextarea
            label="system prompt"
            rows={6}
            value={serverForm.prompt_instructions}
            onChange={(e) =>
              setServerForm({ ...serverForm, prompt_instructions: e.target.value })
            }
            helper="prepended to every session against this server"
            placeholder="avoid destructive commands. always ask before restarting services."
          />
          <label className="flex items-center gap-2 cursor-pointer select-none font-mono text-[14px] text-fg">
            <input
              type="checkbox"
              checked={serverForm.browser_enabled}
              onChange={(e) =>
                setServerForm({ ...serverForm, browser_enabled: e.target.checked })
              }
              className="h-4 w-4 accent-accent"
            />
            <span className="uppercase tracking-[0.08em] text-[12px]">browser testing</span>
          </label>
          <HairlineInput
            label="browser entry url"
            value={serverForm.browser_entry_url}
            onChange={(e) =>
              setServerForm({ ...serverForm, browser_entry_url: e.target.value })
            }
            helper="optional preferred URL for Chrome DevTools MCP when testing this server"
            placeholder="https://example.com"
            disabled={!serverForm.browser_enabled}
          />
          <label className="flex items-center gap-2 cursor-pointer select-none font-mono text-[14px] text-fg">
            <input
              type="checkbox"
              checked={serverForm.is_active}
              onChange={(e) => setServerForm({ ...serverForm, is_active: e.target.checked })}
              className="h-4 w-4 accent-accent"
            />
            <span className="uppercase tracking-[0.08em] text-[12px]">active</span>
          </label>
          <div className="pt-3 flex items-center justify-between border-t border-line">
            <button
              onClick={() => setOpenServerPanel(false)}
              className="btn btn-ghost"
            >
              <span className="text-fg-dim">[</span>cancel<span className="text-fg-dim">]</span>
            </button>
            <button
              onClick={submitServer}
              disabled={busy || !serverForm.host.trim()}
              className="btn btn-primary"
            >
              <span className="text-fg-dim">[</span>
              {editingServer ? 'save' : 'add server'}
              <span className="text-fg-dim">]</span>
            </button>
          </div>
        </div>
      </SidePanel>

      {/* User panel */}
      <SidePanel
        open={openUserPanel}
        onClose={() => setOpenUserPanel(false)}
        title={editingUser ? 'edit user' : 'add user'}
        subtitle={
          editingUser
            ? `${editingUser.email} · #${editingUser.id}`
            : undefined
        }
      >
        <div className="space-y-4">
          {!editingUser && (
            <>
              <HairlineInput
                label="email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="new.user@example.com"
              />
              <HairlineInput
                label="initial password"
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                helper="share with the user on first sign-in"
              />
            </>
          )}

          <div className="space-y-1.5">
            <label className="block uppercase tracking-[0.08em] text-[11.5px] text-fg-mute">
              role
            </label>
            <div className="inline-flex items-center gap-0 border border-line">
              {(['user', 'admin'] as const).map((role) => (
                <button
                  key={role}
                  onClick={() => setUserForm({ ...userForm, role })}
                  className={`px-3 py-1 font-mono text-[13px] transition-colors ${
                    userForm.role === role
                      ? 'bg-accent text-bg'
                      : 'text-fg hover:bg-surface'
                  }`}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block uppercase tracking-[0.08em] text-[11.5px] text-fg-mute">
              server access
            </label>
            <div className="border border-line max-h-56 overflow-y-auto">
              {servers.length === 0 && (
                <div className="px-3 py-6 text-center text-fg-mute text-[13px]">
                  // no servers yet
                </div>
              )}
              {servers.map((s, i) => {
                const checked = userForm.server_ids.includes(s.id)
                return (
                  <label
                    key={s.id}
                    className={`flex items-center gap-2 px-3 py-1.5 hover:bg-surface cursor-pointer ${
                      i > 0 ? 'border-t border-line' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...userForm.server_ids, s.id]
                          : userForm.server_ids.filter((id) => id !== s.id)
                        setUserForm({ ...userForm, server_ids: next })
                      }}
                      className="h-4 w-4 accent-accent"
                    />
                    <span className="font-mono text-[14px] text-fg">{s.host}</span>
                    {s.role && (
                      <span className="font-mono text-[12px] text-fg-dim">· {s.role}</span>
                    )}
                  </label>
                )
              })}
            </div>
          </div>

          {editingUser && (
            <div className="pt-3 border-t border-line">
              <HairlineInput
                label="set new password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                helper="leave blank to keep existing"
                placeholder="new password"
              />
            </div>
          )}

          <div className="pt-3 flex items-center justify-between border-t border-line">
            <button
              onClick={() => setOpenUserPanel(false)}
              className="btn btn-ghost"
            >
              <span className="text-fg-dim">[</span>cancel<span className="text-fg-dim">]</span>
            </button>
            <button
              onClick={submitUser}
              disabled={
                busy ||
                (!editingUser && (!userForm.email.trim() || !userForm.password))
              }
              className="btn btn-primary"
            >
              <span className="text-fg-dim">[</span>
              {editingUser ? 'save' : 'add user'}
              <span className="text-fg-dim">]</span>
            </button>
          </div>
        </div>
      </SidePanel>
    </AppShell>
  )
}

function TabBtn({
  active,
  onClick,
  count,
  children,
}: {
  active: boolean
  onClick: () => void
  count?: number
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={`px-3 py-1.5 font-mono text-[13px] flex items-baseline gap-2 transition-colors ${
        active ? 'bg-accent text-bg' : 'text-fg-mute hover:bg-surface hover:text-fg'
      }`}
    >
      <span>{children}</span>
      {typeof count === 'number' && (
        <span
          className={`text-[11.5px] tnum ${active ? 'text-bg/70' : 'text-fg-dim'}`}
        >
          [{count}]
        </span>
      )}
    </button>
  )
}

function SettingsPanel({
  sharedBasePrompt,
  updatedAt,
  busy,
  onChange,
  onSave,
}: {
  sharedBasePrompt: string
  updatedAt: string | null
  busy: boolean
  onChange: (value: string) => void
  onSave: () => void
}) {
  return (
    <div className="max-w-4xl">
      <div className="mb-3 text-[14px] text-fg-mute">
        // shared prompt applied to all servers on every message
      </div>
      <div className="border border-line p-4 md:p-5 space-y-4">
        <HairlineTextarea
          label="shared base prompt"
          rows={12}
          value={sharedBasePrompt}
          onChange={(e) => onChange(e.target.value)}
          helper="combined before per-server instructions and sent as part of the system prompt on every turn"
          placeholder="always confirm before destructive changes. prefer read-only diagnostics first."
        />
        <div className="text-[12px] text-fg-dim">
          {updatedAt ? `last updated: ${new Date(updatedAt).toLocaleString()}` : 'not saved yet'}
        </div>
        <div className="pt-3 flex items-center justify-end border-t border-line">
          <button onClick={onSave} disabled={busy} className="btn btn-primary">
            <span className="text-fg-dim">[</span>save settings<span className="text-fg-dim">]</span>
          </button>
        </div>
      </div>
    </div>
  )
}

/* --------------------------------------------------------------------- *
 *  SERVERS TABLE
 * --------------------------------------------------------------------- */
function ServersTable({
  rows,
  onNew,
  onEdit,
  onDelete,
}: {
  rows: AdminServer[]
  onNew: () => void
  onEdit: (b: AdminServer) => void
  onDelete: (id: number) => void
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[14px] text-fg-mute">
          {rows.length === 0
            ? '// no servers yet'
            : `// ${rows.length} server${rows.length === 1 ? '' : 's'}`}
        </div>
        <button onClick={onNew} className="btn btn-primary">
          <span className="text-fg-dim">[</span>
          <span>+</span>
          <span>add server</span>
          <span className="text-fg-dim">]</span>
        </button>
      </div>
      <div className="border border-line overflow-x-auto">
        <div className="grid grid-cols-[48px_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_100px_120px] gap-3 px-3 py-1.5 border-b border-line bg-surface text-fg-dim uppercase tracking-[0.08em] text-[11px]">
          <div>id</div>
          <div>host</div>
          <div>role</div>
          <div>notes</div>
          <div>status</div>
          <div className="text-right">actions</div>
        </div>
        {rows.length === 0 && (
          <div className="px-4 py-10 text-center text-fg-dim text-[14px]">
            // no servers yet. add your first server to start a session.
          </div>
        )}
        {rows.map((b, idx) => (
          <div
            key={b.id}
            className={`grid grid-cols-[48px_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_100px_120px] gap-3 px-3 py-2 items-center group hover:bg-surface transition-colors text-[14px] ${
              idx === rows.length - 1 ? '' : 'border-b border-line'
            }`}
          >
            <div className="text-fg-dim tnum">{String(b.id).padStart(3, '0')}</div>
            <div className="min-w-0">
              <div className="text-fg truncate">{b.host}</div>
              <div className="text-[11.5px] text-fg-dim truncate">
                {b.ssh_user}@{b.host}:{b.ssh_port}
              </div>
            </div>
            <div className="text-fg-soft truncate">{b.role || '—'}</div>
            <div className="text-fg-mute truncate">{b.notes || '—'}</div>
            <div>
              <span
                className={`inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.08em] px-1.5 py-0.5 border ${
                  b.is_active
                    ? 'border-accent text-accent'
                    : 'border-line text-fg-dim'
                }`}
              >
                <StatusDot tone={b.is_active ? 'accent' : 'dim'} size={6} />
                {b.is_active ? 'active' : 'inactive'}
              </span>
            </div>
            <div className="flex items-center justify-end gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
              <button onClick={() => onEdit(b)} className="btn !h-5 !px-1.5 !text-[11px]">
                <span className="text-fg-dim">[</span>edit<span className="text-fg-dim">]</span>
              </button>
              <button
                onClick={() => onDelete(b.id)}
                aria-label="Delete this server"
                className="btn btn-danger !h-5 !px-1.5 !text-[11px]"
              >
                <span className="text-fg-dim">[</span>rm<span className="text-fg-dim">]</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* --------------------------------------------------------------------- *
 *  USERS TABLE
 * --------------------------------------------------------------------- */
function UsersTable({
  rows,
  serverLookup,
  onNew,
  onEdit,
  onToggle,
}: {
  rows: AdminUser[]
  serverLookup: Map<number, AdminServer>
  onNew: () => void
  onEdit: (e: AdminUser) => void
  onToggle: (e: AdminUser) => void
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[14px] text-fg-mute">
          {rows.length === 0
            ? '// no users yet'
            : `// ${rows.length} user${rows.length === 1 ? '' : 's'}`}
        </div>
        <button onClick={onNew} className="btn btn-primary">
          <span className="text-fg-dim">[</span>
          <span>+</span>
          <span>add user</span>
          <span className="text-fg-dim">]</span>
        </button>
      </div>
      <div className="border border-line overflow-x-auto">
        <div className="grid grid-cols-[48px_minmax(0,1.5fr)_100px_120px_minmax(0,1.25fr)_100px_140px] gap-3 px-3 py-1.5 border-b border-line bg-surface text-fg-dim uppercase tracking-[0.08em] text-[11px]">
          <div>id</div>
          <div>email</div>
          <div>role</div>
          <div>2fa</div>
          <div>server access</div>
          <div>status</div>
          <div className="text-right">actions</div>
        </div>
        {rows.length === 0 && (
          <div className="px-4 py-10 text-center text-fg-dim text-[14px]">
            // no users yet. add your first user to grant fleet access.
          </div>
        )}
        {rows.map((u, idx) => {
          const disabled = !!u.disabled_at
          const hosts = u.server_ids
            .map((id) => serverLookup.get(id)?.host)
            .filter(Boolean) as string[]
          return (
            <div
              key={u.id}
              className={`grid grid-cols-[48px_minmax(0,1.5fr)_100px_120px_minmax(0,1.25fr)_100px_140px] gap-3 px-3 py-2 items-center group hover:bg-surface transition-colors text-[14px] ${
                idx === rows.length - 1 ? '' : 'border-b border-line'
              }`}
            >
              <div className="text-fg-dim tnum">{String(u.id).padStart(3, '0')}</div>
              <div className="text-fg truncate">{u.email}</div>
              <div>
                <span
                  className={`inline-flex items-center text-[11.5px] uppercase tracking-[0.08em] px-1.5 py-0.5 border ${
                    u.role === 'admin'
                      ? 'border-accent text-accent'
                      : 'border-line text-fg'
                  }`}
                >
                  {u.role}
                </span>
              </div>
              <div>
                <span
                  className={`inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.08em] px-1.5 py-0.5 border ${twoFactorTone(u.two_factor_status)}`}
                >
                  <StatusDot tone={twoFactorDotTone(u.two_factor_status)} size={6} />
                  {twoFactorLabel(u.two_factor_status)}
                </span>
              </div>
              <div className="text-fg-mute truncate">
                {hosts.length === 0 ? '—' : hosts.join(', ')}
              </div>
              <div>
                <span
                  className={`inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.08em] px-1.5 py-0.5 border ${
                    disabled
                      ? 'border-line text-fg-dim'
                      : 'border-accent text-accent'
                  }`}
                >
                  <StatusDot tone={disabled ? 'dim' : 'accent'} size={6} />
                  {disabled ? 'disabled' : 'active'}
                </span>
              </div>
              <div className="flex items-center justify-end gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
                <button onClick={() => onEdit(u)} className="btn !h-5 !px-1.5 !text-[11px]">
                  <span className="text-fg-dim">[</span>edit<span className="text-fg-dim">]</span>
                </button>
                <button
                  onClick={() => onToggle(u)}
                  aria-label={disabled ? 'Enable this user' : 'Disable this user'}
                  className={`btn !h-5 !px-1.5 !text-[11px] ${disabled ? 'btn-primary' : 'btn-danger'}`}
                >
                  <span className="text-fg-dim">[</span>
                  {disabled ? 'enable' : 'disable'}
                  <span className="text-fg-dim">]</span>
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function twoFactorLabel(status: TwoFactorStatus): string {
  switch (status) {
    case 'enabled':
      return 'enabled'
    case 'pending_setup':
      return 'pending'
    case 'not_enrolled':
      return 'not set'
  }
}

function twoFactorTone(status: TwoFactorStatus): string {
  switch (status) {
    case 'enabled':
      return 'border-accent text-accent'
    case 'pending_setup':
      return 'border-warn text-warn'
    case 'not_enrolled':
      return 'border-line text-fg-dim'
  }
}

function twoFactorDotTone(status: TwoFactorStatus): 'accent' | 'warn' | 'dim' {
  switch (status) {
    case 'enabled':
      return 'accent'
    case 'pending_setup':
      return 'warn'
    case 'not_enrolled':
      return 'dim'
  }
}
